import { useState, useCallback, useRef } from 'react';
import SectionTitle from '@/components/base/SectionTitle';

const equipementPro = [
  {
    icon: 'ri-beer-line',
    title: '2 tireuses à bière tropicalisées à triple robinets',
    desc: "Jusqu'à 1000 bières / heure — fûts directement raccordés à la chambre froide",
  },
  {
    icon: 'ri-fridge-line',
    title: '2 frigos doubles à portes coulissantes',
    desc: 'Capacité 1760 litres — environ 1000 bouteilles 50 cl',
  },
  {
    icon: 'ri-snowflake-line',
    title: 'Chambre froide intégrée',
    desc: 'Capacité 3 palettes',
  },
  {
    icon: 'ri-water-flash-line',
    title: 'Lavabo avec eau chaude',
    desc: 'Intégré au plan de travail avec rangement',
  },
];

const optimisation = [
  {
    icon: 'ri-archive-2-line',
    title: 'Espaces de stockage optimisés',
    desc: 'Rangement discret pour matériel, vaisselle et déchets',
  },
  {
    icon: 'ri-safe-2-line',
    title: '2 tiroirs-caisses intégrés',
    desc: 'Dissimulés pour plus de sécurité',
  },
  {
    icon: 'ri-plug-line',
    title: 'Prises électriques intégrées',
    desc: '',
  },
  {
    icon: 'ri-battery-line',
    title: 'Alimentation 16A triphasé',
    desc: '',
  },
];

const slides = [
  {
    type: 'video' as const,
    src: 'https://storage.helloreaddy.io/project_files/d8db5950-dd7c-40d9-b2de-72ddcdf21388/80555c3b-efdd-43a4-a272-1f4fa09bfdfc_video-remorque.mp4',
    alt: 'Vidéo de la remorque bar mobile en action',
  },
  {
    src: 'https://storage.helloreaddy.io/project_files/d8db5950-dd7c-40d9-b2de-72ddcdf21388/056a196d-3f11-42cf-824a-f24815a3aff5_compressed_aubemedia_BeachFoodFestival26-176.webp',
    alt: 'La remorque Beach Food Festival en action – vue latérale complète',
  },
  {
    src: 'https://storage.helloreaddy.io/project_files/d8db5950-dd7c-40d9-b2de-72ddcdf21388/251ed210-e14f-47e0-b82c-2bcf9c4aa319_compressed_aubemedia_BeachFoodFestival26-155.webp',
    alt: 'Tireuse à bière en service – pression dans un verre',
  },
  {
    src: 'https://storage.helloreaddy.io/project_files/d8db5950-dd7c-40d9-b2de-72ddcdf21388/be0000f2-69a2-41e0-80ef-e8d0e361979e_compressed_WhatsApp-Image-2026-08-17-at-17.39.51.webp',
    alt: 'Remorque personnalisée Beach Food Festival – vue arrière',
  },
  {
    src: 'https://storage.helloreaddy.io/project_files/d8db5950-dd7c-40d9-b2de-72ddcdf21388/a2004695-c753-42a0-bd46-2d1c038744cd_compressed_WhatsApp-Image-2026-08-17-at-17.39.52.webp',
    alt: 'Intérieur de la remorque bar – comptoir et tireuses',
  },
  {
    src: 'https://storage.helloreaddy.io/project_files/d8db5950-dd7c-40d9-b2de-72ddcdf21388/976b180b-abd3-49b5-a02d-df025b1c20e7_compressed_WhatsApp-Image-2026-08-17-at-17.52.56.webp',
    alt: 'Intérieur de la remorque bar – frigos professionnels',
  },
  {
    src: 'https://storage.helloreaddy.io/project_files/d8db5950-dd7c-40d9-b2de-72ddcdf21388/5f434110-380e-49e6-a050-56b026fca149_compressed_WhatsApp-Image-2026-08-17-at-17.57.29.webp',
    alt: 'Chambre froide intégrée de la remorque bar',
  },
];

export default function Remorque() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const videoPoster =
    'https://storage.helloreaddy.io/project_files/d8db5950-dd7c-40d9-b2de-72ddcdf21388/056a196d-3f11-42cf-824a-f24815a3aff5_compressed_aubemedia_BeachFoodFestival26-176.webp';

  const handleVideoPlay = () => {
    const video = videoRef.current;
    if (!video) return;
    video.play().then(() => setIsVideoPlaying(true)).catch(() => setIsVideoPlaying(false));
  };

  const goNext = useCallback(() => {
    setCurrentIndex((prev) => Math.min(prev + 1, slides.length - 1));
  }, []);

  const goPrev = useCallback(() => {
    setCurrentIndex((prev) => Math.max(prev - 1, 0));
  }, []);

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (touchStart - touchEnd > 50) {
      goNext();
    }
    if (touchStart - touchEnd < -50) {
      goPrev();
    }
  };

  return (
    <section id="remorque" className="pt-10 pb-4 sm:py-28 lg:py-16 bg-[#fbf9f4]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionTitle
          label="Notre Équipement"
          title="La Remorque"
          delay={100}
        />
        <div className="text-center mb-4 lg:mb-12">
          <div className="inline-flex items-center gap-3 bg-[#00A8B5]/10 border border-[#00A8B5]/20 rounded-full px-6 py-3 animate-fade-in-up delay-200">
            <span className="text-2xl sm:text-3xl font-extrabold text-[#00A8B5]">3</span>
            <span className="text-base sm:text-lg font-bold text-gray-800">remorques disponibles</span>
          </div>
        </div>
        <div className="flex flex-col gap-12 lg:gap-16">
          {/* Carrousel photos remorque */}
          <div className="relative w-full animate-fade-in-up delay-200">
            <div
              className="relative rounded-lg overflow-hidden"
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              <div
                className="flex transition-transform duration-500 ease-out"
                style={{ transform: `translateX(-${currentIndex * 100}%)` }}
              >
                {slides.map((slide) => (
                  <div key={slide.src} className="w-full flex-shrink-0 relative">
                    {slide.type === 'video' ? (
                      <>
                        <video
                          ref={(el) => (videoRef.current = el)}
                          src={slide.src}
                          poster={videoPoster}
                          autoPlay
                          muted
                          loop
                          playsInline
                          onPlay={() => setIsVideoPlaying(true)}
                          onPause={() => setIsVideoPlaying(false)}
                          onEnded={() => setIsVideoPlaying(false)}
                          className="w-full h-auto max-h-[480px] object-contain object-center"
                        />
                        {!isVideoPlaying && (
                          <button
                            onClick={handleVideoPlay}
                            className="absolute inset-0 z-10 flex items-center justify-center cursor-pointer"
                            aria-label="Lancer la vidéo"
                          >
                            <span className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-white/90 backdrop-blur-sm border border-gray-200/60 shadow-md flex items-center justify-center text-[#00A8B5] hover:bg-[#00A8B5] hover:text-white hover:border-[#00A8B5] transition-all">
                              <i className="ri-play-fill text-3xl md:text-4xl"></i>
                            </span>
                          </button>
                        )}
                      </>
                    ) : (
                      <img
                        src={slide.src}
                        alt={slide.alt}
                        className="w-full h-auto max-h-[480px] object-contain object-center"
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Flèche gauche */}
            <button
              onClick={goPrev}
              disabled={currentIndex === 0}
              className="absolute left-2 md:left-3 top-1/2 -translate-y-1/2 z-10 w-9 h-9 md:w-10 md:h-10 rounded-full bg-white/90 backdrop-blur-sm border border-gray-200/60 shadow-md flex items-center justify-center text-[#00A8B5] hover:bg-[#00A8B5] hover:text-white hover:border-[#00A8B5] transition-all disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
              aria-label="Image précédente"
            >
              <i className="ri-arrow-left-s-line text-lg md:text-xl"></i>
            </button>

            {/* Flèche droite */}
            <button
              onClick={goNext}
              disabled={currentIndex === slides.length - 1}
              className="absolute right-2 md:right-3 top-1/2 -translate-y-1/2 z-10 w-9 h-9 md:w-10 md:h-10 rounded-full bg-white/90 backdrop-blur-sm border border-gray-200/60 shadow-md flex items-center justify-center text-[#00A8B5] hover:bg-[#00A8B5] hover:text-white hover:border-[#00A8B5] transition-all disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
              aria-label="Image suivante"
            >
              <i className="ri-arrow-right-s-line text-lg md:text-xl"></i>
            </button>

            {/* Indicateurs */}
            <div className="flex justify-center items-center gap-2 mt-5">
              {slides.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentIndex(i)}
                  className={`rounded-full transition-all cursor-pointer ${
                    i === currentIndex
                      ? 'bg-[#00A8B5] w-6 h-2'
                      : 'bg-[#00A8B5]/30 w-2 h-2 hover:bg-[#00A8B5]/50'
                  }`}
                  aria-label={`Aller à l'image ${i + 1}`}
                />
              ))}
            </div>
          </div>

          {/* 2 blocs côte à côte */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-16">
            {/* Bloc 1 : Équipement professionnel */}
            <div>
              <h3 className="text-2xl font-semibold text-[#00A8B5] mb-6 lg:mb-8">Équipement professionnel</h3>
              <div className="flex flex-col gap-5 lg:gap-6">
                {equipementPro.map((item) => (
                  <div key={item.title} className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-[#00A8B5]/10 flex items-center justify-center flex-shrink-0">
                      <i className={`${item.icon} text-[#00A8B5] text-xl`}></i>
                    </div>
                    <div>
                      <p className="text-gray-900 font-bold text-base leading-snug">{item.title}</p>
                      <p className="text-gray-600 text-sm mt-0.5">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Bloc 2 : Optimisation & praticité */}
            <div>
              <h3 className="text-2xl font-semibold text-[#00A8B5] mb-6 lg:mb-8">Optimisation & praticité</h3>
              <div className="flex flex-col gap-5 lg:gap-6">
                {optimisation.map((item) => (
                  <div key={item.title} className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-[#00A8B5]/10 flex items-center justify-center flex-shrink-0">
                      <i className={`${item.icon} text-[#00A8B5] text-xl`}></i>
                    </div>
                    <div>
                      <p className="text-gray-900 font-bold text-base leading-snug">{item.title}</p>
                      {item.desc && <p className="text-gray-600 text-sm mt-0.5">{item.desc}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Plan de la remorque */}
          <div className="animate-fade-in-up delay-300">
            <h3 className="text-2xl font-semibold text-[#00A8B5] mb-2 lg:mb-8 text-center">Dimensions de la remorque</h3>
            <img
              src="https://static.readdy.ai/image/254cde0d10dfb5832a5e0a0044c661fc/f6c60489936b32f40a7534705129e6a7.png"
              alt="Vue du dessus de la remorque bar mobile"
              className="w-full h-80 sm:h-[28rem] object-contain object-center rounded-lg"
            />
          </div>
        </div>
      </div>
    </section>
  );
}